/**
 * Shared Google tag (gtag.js) bootstrap — SINGLE SOURCE OF TRUTH.
 *
 * Verified live setup (Google UI + public tag resources, Sep 2026):
 *   - THE TAGS ARE NO LONGER COMBINED. `AW-18431332404` was split into its own
 *     "Magic Builders — Google Ads" tag and the Ads destination was reassigned
 *     to it. The public tag resources now confirm `G-E0GWX16DYC` serves the
 *     GA4 destination only, and `AW-18431332404` the Ads destination only.
 *   - Existing aliases `GT-MQ7X88LV` / `GT-KFNBDSV2` remain GA4.
 *   - Automatic user-provided data collection is disabled on BOTH tags;
 *     enhanced conversions stay off. GA4 Google Signals is disabled in all
 *     regions, and GA4 "page views on browser history change" is saved OFF.
 *   - GTM container `GTM-WGFZFB8R` (v6) is Meta-only and is NOT part of this
 *     bootstrap; it runs on its own `window.marketingDataLayer`
 *     (see `src/lib/marketing-datalayer.ts`).
 *
 * Because the destinations are now SEPARATE tags, this loader must not rely on
 * one tag's remote config serving the other. Each destination gets its own
 * `gtag/js` loader request (gtag.js de-duplicates the library itself) and its
 * own explicit `config`, so an analytics-only visitor never initializes the
 * Ads destination and an advertising-only visitor never initializes GA4.
 *
 * Order independence is a requirement, not a side effect: analytics-then-ads
 * and ads-then-analytics grants converge on the same state, and re-running any
 * step is a no-op. A DENIED destination is never initialized.
 *
 * Explicit routing is kept everywhere: every measurement call names its
 * destination through `send_to`.
 *
 * Nothing in this module grants consent, enables enhanced conversions, or
 * turns on any additional data collection.
 */

/** GA4 destination (analytics consent). */
export const GA4_MEASUREMENT_ID = "G-E0GWX16DYC";

const TAG_SCRIPT_ATTR = "data-lovable-google-tag";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer?: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
  }
}

/** Destination ids already configured this page load. */
const configured = new Set<string>();
/** Destination ids whose loader script has already been requested. */
const scriptsRequested = new Set<string>();
let jsCommandSent = false;

/** Reset module state. Test-only. */
export function __resetGoogleTagForTests(): void {
  configured.clear();
  scriptsRequested.clear();
  jsCommandSent = false;
}

/**
 * The official gtag shim. It MUST push the real `arguments` object (not a
 * copied array): gtag.js reads `arguments`-shaped entries out of the queue and
 * ignores plain arrays, which is why a spread-into-array shim silently drops
 * every queued command.
 */
export function ensureGtag(): ((...args: unknown[]) => void) | null {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  return window.gtag as (...args: unknown[]) => void;
}

/** True when a loader script for THIS destination id is already in the page. */
function scriptAlreadyPresent(id: string): boolean {
  if (typeof document === "undefined") return false;
  const escaped = id.replace(/"/g, '\\"');
  if (document.querySelector(`script[${TAG_SCRIPT_ATTR}="${escaped}"]`)) return true;
  // Also honour a script inserted by anything else (older builds, a snippet
  // pasted into the host) for the same id, so we never load one id twice.
  return Boolean(
    document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${escaped}"]`),
  );
}

/**
 * Insert the loader script for ONE destination. The tags are separate now, so
 * each consented destination gets its own loader request; gtag.js de-duplicates
 * the library itself. Safe to call repeatedly and from either consent path.
 */
function ensureTagScript(id: string): void {
  if (typeof document === "undefined") return;
  if (scriptsRequested.has(id) || scriptAlreadyPresent(id)) {
    scriptsRequested.add(id);
    return;
  }
  scriptsRequested.add(id);
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  s.setAttribute(TAG_SCRIPT_ATTR, id);
  document.head.appendChild(s);
}

/**
 * Load the loader for ONE consented destination (once) and configure that
 * destination (once). Never call this for a destination the visitor denied.
 *
 * @param id      GA4 `G-…` or Ads `AW-…` destination id.
 * @param params  Config params for THIS destination only.
 * @returns true when the destination is configured and usable.
 */
export function configureGoogleTagDestination(
  id: string,
  params: Record<string, unknown> = {},
): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (!id) return false;
  const gtag = ensureGtag();
  if (!gtag) return false;

  ensureTagScript(id);

  if (configured.has(id)) return true;
  if (!jsCommandSent) {
    jsCommandSent = true;
    gtag("js", new Date());
  }
  gtag("config", id, params);
  configured.add(id);
  return true;
}

/** Destination ids configured this page load (Google Ads + GA4). */
export function getConfiguredDestinations(): string[] {
  return Array.from(configured);
}

/**
 * Re-send `config` for an ALREADY configured destination with the documented
 * `update: true` flag, so refreshed defaults (page path/title/referrer,
 * campaign fields) apply to the tag's own automatic collection WITHOUT
 * generating an extra page view.
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/config
 */
export function updateGoogleTagDestination(
  id: string,
  params: Record<string, unknown> = {},
): boolean {
  if (typeof window === "undefined") return false;
  if (!configured.has(id)) return false;
  const gtag = ensureGtag();
  if (!gtag) return false;
  gtag("config", id, { ...params, update: true, send_page_view: false });
  return true;
}

/** True when this destination has already been configured this page load. */
export function isDestinationConfigured(id: string): boolean {
  return configured.has(id);
}
